import { Moment } from 'moment-timezone'
import React, { useEffect, useRef, useState, useLayoutEffect } from 'react'

import { useCurrentTime } from 'shared/contexts'
import { scrollToDate, scrollToToday, type MeetingGroup } from 'shared/utils'

/**
 * Checks if the code is running in a browser environment (not SSR or tests).
 * @returns true if window and document are defined, false otherwise
 */
const isBrowserEnvironment = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined'

/**
 * Hook to track scroll position and restore it when meetings change.
 * Useful for maintaining scroll position when new meetings are loaded.
 *
 * @param meetings - Array of meeting groups
 * @param callback - Callback function that receives scroll data { top, height }
 * @returns Function to set scroll data
 */
export const useScrollPosition = (meetings: MeetingGroup[], callback: (scrollData: { top: number; height: number }) => void) => {
  const [scrollData, setScrollData] = useState<{ top: number; height: number } | null>(null)
  const prevMeetings = useRef(meetings)

  useLayoutEffect(() => {
    if (meetings === prevMeetings.current) return
    prevMeetings.current = meetings

    if (!scrollData) return

    callback(scrollData)
    setScrollData(null)
    prevMeetings.current = meetings
  }, [meetings, scrollData, callback])

  return setScrollData
}

/**
 * Hook to perform initial scroll to today's date when meetings are first loaded.
 * Only scrolls once per component mount.
 *
 * @param containerRef - Ref to the scrollable container
 * @param meetings - Array of meeting groups
 * @param timezone - Timezone string (e.g., 'America/New_York')
 * @param currentTime - Optional current time moment object (if not provided, uses useCurrentTime hook)
 */
export const useInitialScroll = (
  containerRef: React.RefObject<HTMLDivElement>,
  meetings: MeetingGroup[],
  timezone: string,
  currentTime?: Moment,
  scrollToDateTarget?: string | null
) => {
  const hookCurrentTime = useCurrentTime()
  const effectiveCurrentTime = currentTime ?? hookCurrentTime
  const effectiveCurrentTimeRef = useRef(effectiveCurrentTime)
  effectiveCurrentTimeRef.current = effectiveCurrentTime

  // Read via ref so a target change doesn't reschedule the one-shot scroll.
  const scrollToDateTargetRef = useRef(scrollToDateTarget)
  scrollToDateTargetRef.current = scrollToDateTarget

  const hasScrolledRef = useRef(false)
  useEffect(() => {
    if (hasScrolledRef.current || !meetings || !Array.isArray(meetings) || !meetings.length) return undefined

    const container = containerRef.current
    if (!container) return undefined

    // Small delay to ensure DOM is fully rendered. Only depend on meetings/timezone/containerRef
    // so we don't reschedule (and jump) when selection/URL causes re-renders with new time ref.
    const timeoutId = setTimeout(() => {
      hasScrolledRef.current = true
      // When the user picked a date in the selector, center on it; otherwise scroll to today.
      // Without this branch the today scroll would override the date scroll on first load.
      if (scrollToDateTargetRef.current) {
        scrollToDate(containerRef, scrollToDateTargetRef.current, true)
      } else {
        scrollToToday(containerRef, meetings, timezone, true, effectiveCurrentTimeRef.current)
      }
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [meetings, timezone, containerRef])
}

/**
 * Hook to disable browser scroll restoration.
 * Prevents the browser from automatically restoring scroll position on page load.
 * Restores the original value when the component unmounts.
 */
export const useDisableScrollRestoration = () => {
  useEffect(() => {
    if (!isBrowserEnvironment()) {
      return
    }

    if ('scrollRestoration' in window.history) {
      // Store the original value to restore on unmount
      const originalValue = window.history.scrollRestoration
      window.history.scrollRestoration = 'manual'

      // Restore the original value when component unmounts
      return () => {
        window.history.scrollRestoration = originalValue
      }
    }
  }, [])
}
